import { generateHash } from './util';
import { EventDispatcher } from './event-dispatcher';

interface TransportReceiveResponse {
  askerId: string;
  payload?: unknown;
}

interface TransportReceiveAsk {
  name: string;
  askerId: string;
  payload?: unknown;
}

interface TransportEvent {
  name: string;
  payload?: unknown;
}

interface PromissedResponse {
  resolve: (value: unknown | PromiseLike<unknown>) => void;
  reject: (reason?: unknown) => void;
}

class Transport extends EventDispatcher<{
  name: string;
  askerId: string;
  payload?: unknown;
  /**
   * Janela que originou a mensagem. Só existe no que chega por `postMessage`.
   *
   * É o que permite responder a quem perguntou — e, nos eventos, saber qual
   * iframe falou: a RootApp escuta a `window` inteira, então `mfe.initialized`
   * pode vir do palco ou de um iframe secundário (o drawer do chat).
   */
  source?: Window;
}> {
  private readonly waitingForResponse: Map<string, PromissedResponse> =
    new Map();

  /**
   * Mensagens desconhecidas cairão aqui
   */
  private readonly _trash: MessageEvent[] = [];

  private readonly target: Window;

  private readonly selfWindow: Window;

  /**
   * Janelas que também recebem eventos, e quais eventos cada uma recebe.
   *
   * O `target` é o MFE montado no palco e recebe tudo. Iframes secundários —
   * hoje o drawer do chat — assinam uma lista fechada: `mfe.unmount` sai por
   * este mesmo canal e derrubaria o chat a cada navegação entre MFEs, então
   * um fan-out cego não serve.
   */
  private readonly extraEventTargets: Map<Window, Set<string>> = new Map();

  /**
   * @param target Pra onde serão enviadas as mensagens [postMessage(...)]
   * @param selfWindow Onde devo escutar as respostas [addEventListener('message', ...)]
   */
  public constructor(target: Window, selfWindow: Window) {
    super();
    this.target = target;
    this.selfWindow = selfWindow;
    this.selfWindow.addEventListener('message', (event) => {
      this.handleEventOrMessageResponse(event);
    });
    this.handleEventOrMessageResponse =
      this.handleEventOrMessageResponse.bind(this);
  }

  private sendMessage(
    message: {
      name?: string;
      payload?: unknown;
      askerId?: string;
    },
    target: Window = this.target,
  ) {
    target.postMessage(message, this.selfWindow.location.origin);
  }

  /**
   * Responde uma pergunta.
   *
   * O destino padrão é o `target` do transporte, mas nem sempre é ele quem
   * pergunta: o RootApp escuta a `window` inteira, então qualquer iframe de
   * mesma origem — o drawer do chat, por exemplo — consegue perguntar. Sem o
   * `target` explícito a resposta iria para o iframe errado e a promise de
   * quem perguntou ficaria pendurada para sempre, sem erro nenhum.
   *
   * Quem chama pelo `onAsk` recebe a janela certa automaticamente.
   */
  public sendResponse(askerId: string, payload?: unknown, target?: Window) {
    this.sendMessage(
      {
        askerId,
        payload,
      },
      target,
    );
  }

  /**
   * Passa a entregar também para `target` os eventos cujo nome esteja em
   * `eventNames`. Chamar de novo para a mesma janela substitui a lista.
   */
  public addEventTarget(target: Window, eventNames: string[]) {
    this.extraEventTargets.set(target, new Set(eventNames));
  }

  public removeEventTarget(target: Window) {
    this.extraEventTargets.delete(target);
  }

  public sendEvent(message: { name: string; payload?: unknown }) {
    this.sendMessage(message);

    this.extraEventTargets.forEach((eventNames, target) => {
      if (!eventNames.has(message.name)) {
        return;
      }

      try {
        this.sendMessage(message, target);
      } catch {
        // Iframe já saiu do DOM: para de tentar em vez de estourar no emissor,
        // que não tem nada a ver com o ciclo de vida do destino.
        this.extraEventTargets.delete(target);
      }
    });
  }

  /**
   * Faz uma pergunta e espera a resposta.
   *
   * O destino padrão é o `target`, mas o mesmo transporte fala com mais de um
   * iframe: o `mfe.mount` precisa chegar na janela que acabou de se anunciar,
   * não sempre no palco.
   */
  public askAndWaitForResponse(
    message: {
      name: string;
      payload?: unknown;
    },
    target?: Window,
  ): Promise<unknown> {
    const id = generateHash().toString();
    this.sendMessage(
      {
        ...message,
        askerId: id,
      },
      target,
    );

    return new Promise((resolve, reject) => {
      this.waitingForResponse.set(id, { resolve, reject });
    });
  }

  /**
   * O segundo argumento é a janela que emitiu o evento, quando conhecida.
   * Handlers antigos que só declaram `payload` seguem funcionando.
   */
  public onEvent(
    name: string,
    callback: (payload?: unknown, source?: Window) => void,
  ) {
    this.addListener(name, (data) => {
      callback(data.payload, data.source);
    });
  }

  public onAsk(
    name: string,
    callback: (ask: {
      payload?: unknown;
      sendResponse: (payload?: unknown) => void;
    }) => void,
  ) {
    this.addListener(name, (data) => {
      const { askerId, payload, source } = data;
      const sendResponseProxy = (payload?: unknown) => {
        this.sendResponse(askerId, payload, source);
      };
      callback({
        payload,
        sendResponse: sendResponseProxy,
      });
    });
  }

  public get trash() {
    return this._trash;
  }

  private handleEventOrMessageResponse(event: MessageEvent) {
    if (this.tryHandleUnsecureEvent(event)) {
      return;
    }

    if (this.tryHandleMessageAsk(event)) {
      return;
    }

    if (this.tryHandleMessageResponse(event)) {
      return;
    }

    if (this.tryHandleEvent(event)) {
      return;
    }

    this.handleTrashEvent(event);
  }

  private tryHandleUnsecureEvent(event: MessageEvent) {
    if (event.origin !== this.selfWindow.location.origin) {
      this.handleTrashEvent(event);
      return true;
    }

    return false;
  }

  private tryHandleMessageAsk(event: MessageEvent) {
    const data = event.data as TransportReceiveAsk;
    const { askerId, name, payload } = data;
    if (typeof askerId === 'string' && typeof name === 'string') {
      this.dispatch({
        name,
        askerId,
        payload,
        source: (event.source as Window | null) ?? undefined,
      });
      return true;
    }
    return false;
  }

  private tryHandleMessageResponse(event: MessageEvent) {
    const data = event.data as TransportReceiveResponse;
    const { askerId, payload } = data;
    if (typeof askerId === 'string') {
      const promissed = this.waitingForResponse.get(askerId);
      if (promissed != null) {
        promissed.resolve(payload);
        this.waitingForResponse.delete(askerId);
        return true;
      }
    }
    return false;
  }

  private tryHandleEvent(event: MessageEvent) {
    const { name, payload } = event.data as TransportEvent;
    if (payload != null && typeof name === 'string') {
      this.dispatch({
        ...event.data,
        source: (event.source as Window | null) ?? undefined,
      });
      return true;
    }
    return false;
  }

  private handleTrashEvent(event: MessageEvent) {
    this._trash.push(event);
  }
}

export { Transport };
